import { createContext } from "react";
import { Nullable, Optional } from "sprint-common-web";

import { ContentDetails } from "../../../../../../common/types/ContentDetails";
import { ContentReferenceType } from "../../../../../../common/types/ContentReferenceType";
import { RecordType } from "../../../../../../common/types/RecordType";
import { CloudFileStatus } from "../../../../common/types/CloudFileStatus";
import { CloudFileDetails } from "../../../CloudFileDetails";
import { CLOUD_FILE_ASSOCIATION_DEFAULT_TAB, CloudFileAssociationTab } from "../tabs/CloudFileAssociationTab";

export interface CloudFileAssociationFormState {
    contentType?: ContentReferenceType;
    contentId?: string;
    versionIds?: string[];
    seasonId?: string;
    episodeId?: string;
    airlineId?: string;

    selectedLanguages: CloudFileAssociationLanguagesFormState;

    selectedVideoTechnicalDetails: CloudFileAssociationVideoTechnicalDetailsFormState;
}

export type CloudFileAssociationContentFormState =
    | CloudFileAssociationContentFormStateMovieSelection
    | CloudFileAssociationContentFormStateTvSeriesSelection
    | CloudFileAssociationContentFormStateAncillarySelection
    | { type: null };

export interface CloudFileAssociationContentFormStateMovieSelection {
    type: ContentReferenceType.Movie;
    id: string;
    versionIds?: string[];
}

export const ensureIsMovieSelection: (
    selection: CloudFileAssociationContentFormState
) => asserts selection is CloudFileAssociationContentFormStateMovieSelection = (
    selection: CloudFileAssociationContentFormState
): asserts selection is CloudFileAssociationContentFormStateMovieSelection => {
    if (selection.type !== ContentReferenceType.Movie) throw Error("Selected content is not referring a Movie");
};

export interface CloudFileAssociationContentFormStateTvSeriesSelection {
    type: ContentReferenceType.TvSeries;
    id: string;
    seasonId?: string;
    episodeId?: string;
    versionIds?: string[];
}

export const ensureIsTvSeriesSelection: (
    selection: CloudFileAssociationContentFormState
) => asserts selection is CloudFileAssociationContentFormStateTvSeriesSelection = (
    selection: CloudFileAssociationContentFormState
): asserts selection is CloudFileAssociationContentFormStateTvSeriesSelection => {
    if (selection.type !== ContentReferenceType.TvSeries) throw Error("Selected content is not referring a TvSeries");
};

export interface CloudFileAssociationContentFormStateAncillarySelection {
    type: ContentReferenceType.Ancillary;
    id: string;
    airlineId?: string;
    versionId?: string;
}

export const ensureIsAncillarySelection: (
    selection: CloudFileAssociationContentFormState
) => asserts selection is CloudFileAssociationContentFormStateAncillarySelection = (
    selection: CloudFileAssociationContentFormState
): asserts selection is CloudFileAssociationContentFormStateAncillarySelection => {
    if (selection.type !== ContentReferenceType.Ancillary)
        throw Error("Selected content is not referring an Ancillary");
};

export interface CloudFileAssociationLanguagesFormState {
    soundtrackLanguageId: Nullable<string>;
    subtitleLanguageId: Nullable<string>;
}

export interface CloudFileAssociationVideoTechnicalDetailsFormState {
    aspectRatioId: Nullable<string>;
    aspectTypeId: Nullable<string>;
}

export interface CloudFileAssociationSectionItem {
    id: string;
    status: CloudFileStatus;
    recordType: RecordType;
    content: Optional<ContentDetails>;
}

export interface CloudFileAssociationContext {
    init: (details: CloudFileDetails, forceDisableEdit?: boolean) => void;
    isContextReady: boolean;

    cloudFileItem?: CloudFileAssociationSectionItem;

    isEditDisabled: boolean;
    disableEdit: () => void;
    enableEdit: () => void;

    currentTab: CloudFileAssociationTab;
    setCurrentTab: (tab: CloudFileAssociationTab) => void;

    isFormValid: boolean;
    setIsFormValid: (isValid: boolean) => void;

    applyChanges: () => void;

    defaultFormValues?: CloudFileAssociationFormState;
    setDefaultFormValues: (content: CloudFileAssociationFormState) => void;

    selectedLanguages: CloudFileAssociationLanguagesFormState;
    setSelectedLanguages: (langs: CloudFileAssociationLanguagesFormState) => void;

    selectedVideoTechnicalDetails: CloudFileAssociationVideoTechnicalDetailsFormState;
    setSelectedVideoTechnicalDetails: (details: CloudFileAssociationVideoTechnicalDetailsFormState) => void;
}

export const defaultCloudFileAssociationContext: CloudFileAssociationContext = {
    init: () => Promise.resolve(),
    isContextReady: false,

    isEditDisabled: false,
    disableEdit: () => {},
    enableEdit: () => {},

    currentTab: CLOUD_FILE_ASSOCIATION_DEFAULT_TAB,
    setCurrentTab: () => {},

    isFormValid: true,
    setIsFormValid: () => {},

    applyChanges: () => {},

    defaultFormValues: undefined,
    setDefaultFormValues: () => {},

    selectedLanguages: {
        subtitleLanguageId: null,
        soundtrackLanguageId: null
    },
    setSelectedLanguages: () => {},

    selectedVideoTechnicalDetails: {
        aspectRatioId: null,
        aspectTypeId: null
    },
    setSelectedVideoTechnicalDetails: () => {}
};

export const CloudFileAssociationContext = createContext<CloudFileAssociationContext>(
    defaultCloudFileAssociationContext
);
